import type { PrefetchedConsentPreferenceData } from '@atlassian/browser-storage-controls/controllers/banner-controls/types';
import { initializePreferencesData } from '@atlassian/browser-storage-controls/controllers/cookie-controls/transformer';
import { serializeConsents } from '@atlassian/browser-storage-controls/consent-hub-service/utils';
import type { ConsentPreference } from '@atlassian/browser-storage-controls/types';

import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { expVal } from '@confluence/feature-experiments';
import { cfetch } from '@confluence/network';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { isLocal } from '@confluence/ssr-utilities/entry-points/env.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { constructCompatibleRequestInit } from '@confluence/network/entry-points/constructCompatibleRequestInit.ts';

export const COOKIE_PREFERENCES_QUERY = 'CookiePreferencesQuery';

type CookiePreferencesPreloadResult = PrefetchedConsentPreferenceData | null | undefined;

declare global {
	interface Window {
		__COOKIE_CONSENT_PREFERENCES__?: PrefetchedConsentPreferenceData | null;
		__PRELOADED_COOKIE_PREFERENCES__?: ConsentPreference | null;
		__COOKIE_CONSENT_PREFERENCES_PRELOAD_PROMISE__?: Promise<CookiePreferencesPreloadResult> | null;
	}
}

const preloadCookiePreferencesForBanner = async (): Promise<CookiePreferencesPreloadResult> => {
	if (window.__COOKIE_CONSENT_PREFERENCES__) {
		return window.__COOKIE_CONSENT_PREFERENCES__;
	}

	const {
		cookiesConsentToken,
		cookiesFallbackToken,
		userId,
		perimeter,
		isBifrostIcRequest,
		environment,
	} = getSSRRenderInputs();

	// If the consent token cookie exists (user has already set their preferences), there is no need to
	// fetch the preferences from ConsentHub.
	if (cookiesConsentToken) {
		window.__COOKIE_CONSENT_PREFERENCES__ = {
			consentToken: cookiesConsentToken,
			...(cookiesFallbackToken ? { fallbackConsentToken: cookiesFallbackToken } : {}),
		};
		return window.__COOKIE_CONSENT_PREFERENCES__;
	}

	if (!userId) {
		// For anonymous users, if only the fallback token exists (the primary consent token has
		// expired but the long-lived fallback is still present), include the fallback as a consent
		// signal so that SSR and client hydration make consistent banner visibility decisions.
		const anonymousConsentToken = serializeConsents({});
		if (!anonymousConsentToken && !cookiesFallbackToken) {
			window.__COOKIE_CONSENT_PREFERENCES__ = null;
			return window.__COOKIE_CONSENT_PREFERENCES__;
		}
		window.__COOKIE_CONSENT_PREFERENCES__ = {
			...(anonymousConsentToken ? { consentToken: anonymousConsentToken } : {}),
			...(cookiesFallbackToken ? { fallbackConsentToken: cookiesFallbackToken } : {}),
		};
		return window.__COOKIE_CONSENT_PREFERENCES__;
	}

	if (
		perimeter === 'fedramp-moderate' ||
		(isBifrostIcRequest === true && expVal('confluence_ssr_bifrost_ic', 'isEnabled', false))
	) {
		window.__COOKIE_CONSENT_PREFERENCES__ = null;
		return window.__COOKIE_CONSENT_PREFERENCES__;
	}

	if (!environment) {
		window.__COOKIE_CONSENT_PREFERENCES__ = null;
		ssrPrepareErrorsClient.submitError(
			new Error('Cannot preload Cookie Consent Preferences: missing environment'),
		);
		return window.__COOKIE_CONSENT_PREFERENCES__;
	}

	const hostEnv = environment === 'PRODUCTION' ? 'prod' : 'staging';

	return SSRMeasures.run(
		'ssr-app/prepare/preloadQuery/fetch:CookieConsentPreferences',
		async () => {
			let result: any = null;
			const isLocalPillar = isLocal();
			const url = isLocalPillar
				? '/gateway/api/consenthub/session/user/consents/cookies'
				: `https://atl-preferences-center-elb.${hostEnv}.atl-paas.net/rest/user/${userId}/consents/cookies`;

			try {
				const res = await cfetch(
					url,
					constructCompatibleRequestInit({
						method: 'GET',
						headers: {
							'Content-Type': 'application/json',
							// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
							...(Boolean(process.env.REACT_SSR) &&
							Boolean(process.env.BIFROST_TEMPLATE_BUILD) &&
							!isLocalPillar
								? { 'x-ssr-service-name': 'atl-preferences-center' }
								: {}),
						},
						credentials: 'include',
					}),
				);
				if (!res.ok) {
					throw new Error(`Cannot load Cookie Consent Preferences: ${res.statusText}`);
				}

				result = await res.json();
			} catch (e) {
				ssrPrepareErrorsClient.submitError(
					new Error(`Fetching cookie preferences failed: ${e.message}`),
				);
			}
			if (result && Object.keys(result).length) {
				window.__COOKIE_CONSENT_PREFERENCES__ = Object.assign({}, result, {
					fallbackConsentToken: cookiesFallbackToken,
				});
				return window.__COOKIE_CONSENT_PREFERENCES__;
			} else {
				window.__COOKIE_CONSENT_PREFERENCES__ = null;
				return window.__COOKIE_CONSENT_PREFERENCES__;
			}
		},
	);
};

const getCookiePreferencesForBanner = (): Promise<CookiePreferencesPreloadResult> => {
	if (window.__COOKIE_CONSENT_PREFERENCES__ !== undefined) {
		return Promise.resolve(window.__COOKIE_CONSENT_PREFERENCES__);
	}

	if (!window.__COOKIE_CONSENT_PREFERENCES_PRELOAD_PROMISE__) {
		window.__COOKIE_CONSENT_PREFERENCES_PRELOAD_PROMISE__ =
			preloadCookiePreferencesForBanner().finally(() => {
				window.__COOKIE_CONSENT_PREFERENCES_PRELOAD_PROMISE__ = null;
			});
	}

	return window.__COOKIE_CONSENT_PREFERENCES_PRELOAD_PROMISE__;
};

export const preloadCookiePreferences = async (): Promise<CookiePreferencesPreloadResult> => {
	const cookiePreferences = await getCookiePreferencesForBanner();

	if (window.__PRELOADED_COOKIE_PREFERENCES__ === undefined) {
		const { prefs } = initializePreferencesData(cookiePreferences ?? undefined);
		window.__PRELOADED_COOKIE_PREFERENCES__ = prefs ?? null;
	}

	return cookiePreferences;
};
